import { useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { SkipLink } from "@/components/ui/SkipLink";
import { LiveRegionProvider } from "@/providers/LiveRegionProvider";
import {
  ProtectedRoute,
  LoadingScreen,
  AdminGuard,
  OwnerGuard,
  AdminOrOwnerGuard,
  OwnerFeatureGuard,
} from "./components/common/index.jsx";
import PDFLoadingFallback from "./components/common/PDFLoadingFallback.jsx";
import AuthLayout from "./components/layouts/AuthLayout";
import DashboardLayout from "./components/layouts/DashboardLayout";
import SigningLayout from "./components/layouts/SigningLayout";
import { useDynamicFont } from "./hooks/useDynamicFont";
import { useLocaleSync } from "./hooks/useLocaleSync";
import AdminCustomPlans from "./pages/admin/CustomPlans";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminInvoices from "./pages/admin/Invoices";
import AdminKnowledgeBase from "./pages/admin/KnowledgeBase";
import AdminOrganizations from "./pages/admin/Organizations";
import AdminPricing from "./pages/admin/Pricing";
import AdminSecurity from "./pages/admin/Security";
import AdminSMS from "./pages/admin/SMS";
import AdminUsers from "./pages/admin/Users";
import ForgotPassword from "./pages/auth/ForgotPassword";
import InviteAccept from "./pages/auth/InviteAccept";
import Login from "./pages/auth/Login";
import Register from "./pages/auth/Register";
import RegistrationSuccess from "./pages/auth/RegistrationSuccess";
import ResetPassword from "./pages/auth/ResetPassword";
import VerifyEmail from "./pages/auth/VerifyEmail";
import ContactGroupsPage from "./pages/contacts/ContactGroupsPage";
import ContactsPage from "./pages/contacts/ContactsPage";
import Activity from "./pages/dashboard/Activity";
import AISettings from "./pages/dashboard/AISettings";
import Analytics from "./pages/dashboard/Analytics";
import APIKeys from "./pages/dashboard/APIKeys";
import BulkSend from "./pages/dashboard/BulkSend";
import ClauseEdit from "./pages/dashboard/ClauseEdit";
import ClauseLibrary from "./pages/dashboard/ClauseLibrary";
import Dashboard from "./pages/dashboard/Dashboard";
import Embed from "./pages/dashboard/Embed";
import EnvelopeDetail from "./pages/dashboard/EnvelopeDetail";
import Envelopes from "./pages/dashboard/Envelopes";
import Folders from "./pages/dashboard/Folders";
import InPersonSigning from "./pages/dashboard/InPersonSigning";
import Integrations from "./pages/dashboard/Integrations";
import KnowledgeBase from "./pages/dashboard/KnowledgeBase";
import MergeKeys from "./pages/dashboard/MergeKeys";
import PaymentSettings from "./pages/dashboard/PaymentSettings";
import Reminders from "./pages/dashboard/Reminders";
import SigningLinks from "./pages/dashboard/SigningLinks";
import Tags from "./pages/dashboard/Tags";
import Templates from "./pages/dashboard/Templates";
import Usage from "./pages/dashboard/Usage";
import Webhooks from "./pages/dashboard/Webhooks";
import ZapierSettings from "./pages/dashboard/ZapierSettings";
// The privacy page lives in DataProtection.jsx on purpose: ad-blocker filter
// lists block resource URLs containing "PrivacyPolicy" (ERR_BLOCKED_BY_CLIENT),
// and in dev vite serves modules by their real file names.
// import PrivacyPolicy from "./pages/legal/DataProtection";
// import ESignDisclosure from "./pages/legal/ESignDisclosure";
// import Subprocessors from "./pages/legal/Subprocessors";
// import TermsOfUse from "./pages/legal/TermsOfUse";
import Onboarding from "./pages/onboarding";
import { OnboardingGuard } from "./pages/onboarding/components";
import AdvancedSecuritySettings from "./pages/settings/AdvancedSecurity";
import {
  BillingLayout,
  BillingOverview,
  BillingPlans,
  BillingAddons,
  BillingCredits,
  BillingUsage,
  BillingInvoices,
  BillingPaymentMethods,
} from "./pages/settings/billing";
import BrandingSettings from "./pages/settings/Branding";
import CertificatesSettings from "./pages/settings/Certificates";
import DataRetentionSettings from "./pages/settings/DataRetention";
import NotificationSettings from "./pages/settings/Notifications";
import OrganizationSettings from "./pages/settings/Organization";
import ProfileSettings from "./pages/settings/Profile";
import RemindersSettings from "./pages/settings/Reminders";
import SecuritySettings from "./pages/settings/Security";
import Settings from "./pages/settings/Settings";
import SMSSettings from "./pages/settings/SMS";
import SSOSettings from "./pages/settings/SSO";
import TeamSettings from "./pages/settings/Team";
import InPersonSigningHost from "./pages/signing/InPersonSigningHost";
import Verification from "./pages/Verification";
import { initializeAuth } from "./services/api";
import { useAuthStore } from "./store/auth.store";
import { useThemeStore } from "./store/theme.store";

// ============================================================================
// LAZY-LOADED PDF COMPONENTS
// These components use react-pdf/pdfjs-dist which is ~1.2MB
// Lazy loading ensures the PDF chunk only loads when needed
// ============================================================================
const EnvelopeCreate = lazy(() => import("./pages/dashboard/EnvelopeCreate"));
const TemplateEdit = lazy(() => import("./pages/dashboard/TemplateEdit"));
const DocumentTools = lazy(() => import("./pages/dashboard/DocumentTools"));
const SigningSession = lazy(() => import("./pages/signing/SigningSession"));
const MobileSigningPage = lazy(
  () => import("./pages/signing/MobileSigningPage"),
);
const SignerVerification = lazy(
  () => import("./pages/signing/SignerVerification"),
);
const PublicSigningLink = lazy(
  () => import("./pages/signing/PublicSigningLink"),
);
const InPersonSession = lazy(() => import("./pages/signing/InPersonSession"));
const InPersonSignerView = lazy(
  () => import("./pages/signing/InPersonSignerView"),
);
const EmbedCreate = lazy(() => import("./pages/embed/EmbedCreate"));
const EmbedSign = lazy(() => import("./pages/embed/EmbedSign"));
// Legal pages are imported statically (not lazy): ad-blocker filter lists
// (cookie/annoyance lists) match privacy-policy-named resources, so a lazy
// chunk like PrivacyPolicy-<hash>.js gets blocked with ERR_BLOCKED_BY_CLIENT
// for affected users. As part of the main bundle they cannot be blocked
// individually. They are small, text-only components.
// Lazy load Document Generation pages
const DocumentGeneration = lazy(
  () => import("./pages/dashboard/DocumentGeneration"),
);
const DocGenTemplateCreate = lazy(
  () => import("./pages/dashboard/DocGenTemplateCreate"),
);
const DocGenGenerate = lazy(() => import("./pages/dashboard/DocGenGenerate"));

// Public legal pages are anonymous by design: they must render without
// attempting session restore (the /auth/refresh call) and must never block
// on the auth loading gate.
const PUBLIC_LEGAL_PATHS = [
  "/terms",
  "/privacy",
  "/electronic-signature-disclosure",
  "/subprocessors",
];

function App() {
  const { isLoading, setLoading, accessToken, user, isAuthenticated } =
    useAuthStore();
  const initTheme = useThemeStore((state) => state.initTheme);
  const { pathname } = useLocation();
  const isPublicLegalPage = PUBLIC_LEGAL_PATHS.includes(pathname);

  // Sync user's locale preference with i18n
  useLocaleSync();

  // Load appropriate Noto font variant based on current language
  const { isLoading: fontLoading } = useDynamicFont();

  // Initialize theme on mount
  useEffect(() => {
    initTheme();
  }, [initTheme]);

  /**
   * Auth Initialization - Hybrid Approach
   *
   * With the hybrid token architecture:
   * - Access token is stored in memory only (lost on page refresh)
   * - Refresh token is in httpOnly cookie (persists)
   *
   * On app load:
   * 1. If we already have valid auth state (just logged in), skip refresh
   * 2. Otherwise, try to restore session via refresh token cookie
   * 3. If failed, user needs to login
   */
  useEffect(() => {
    const initAuth = async () => {
      // Anonymous legal pages never restore a session
      if (isPublicLegalPage) {
        setLoading(false);
        return;
      }

      // If we already have a valid session in memory (e.g., just logged in),
      // don't try to refresh - just mark loading as complete
      if (accessToken && user && isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        // Try to restore session via httpOnly refresh token cookie
        // This calls /api/v1/auth/refresh - cookie sent automatically
        await initializeAuth();
      } catch {
        // Error already handled in initializeAuth
      }
    };

    initAuth();
  }, [accessToken, isAuthenticated, setLoading, user, isPublicLegalPage]);

  if ((isLoading && !isPublicLegalPage) || fontLoading) {
    return <LoadingScreen />;
  }

  return (
    <LiveRegionProvider>
      <SkipLink />
      <Routes>
        {/* Auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route
            path="/registration-success"
            element={<RegistrationSuccess />}
          />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
        </Route>

        {/* Onboarding flow - protected but outside normal layout */}
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute>
              <Onboarding />
            </ProtectedRoute>
          }
        />

        {/* Protected dashboard routes */}
        <Route
          element={
            <ProtectedRoute>
              <OnboardingGuard>
                <DashboardLayout />
              </OnboardingGuard>
            </ProtectedRoute>
          }
        >
          {/* Main navigation - all authenticated users */}
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/envelopes" element={<Envelopes />} />
          <Route
            path="/envelopes/new"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <EnvelopeCreate />
              </Suspense>
            }
          />
          <Route path="/envelopes/:id" element={<EnvelopeDetail />} />
          <Route
            path="/envelopes/:id/edit"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <EnvelopeCreate />
              </Suspense>
            }
          />
          <Route
            path="/envelopes/:envelopeId/in-person"
            element={<InPersonSigning />}
          />
          <Route
            path="/envelopes/:envelopeId/signing-links"
            element={<SigningLinks />}
          />
          <Route path="/signing-links" element={<SigningLinks />} />
          <Route path="/in-person" element={<InPersonSigning />} />
          <Route path="/templates" element={<Templates />} />
          <Route
            path="/templates/new"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <TemplateEdit />
              </Suspense>
            }
          />
          <Route
            path="/templates/:id/edit"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <TemplateEdit />
              </Suspense>
            }
          />
          <Route path="/folders" element={<Folders />} />
          <Route path="/tags" element={<Tags />} />
          <Route path="/contacts" element={<ContactsPage />} />
          <Route path="/contacts/groups" element={<ContactGroupsPage />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/activity" element={<Activity />} />
          <Route path="/reminders" element={<Reminders />} />
          <Route path="/analytics" element={<Analytics />} />
          <Route path="/bulk-send" element={<BulkSend />} />
          <Route path="/clause-library" element={<ClauseLibrary />} />
          <Route path="/clause-library/:id/edit" element={<ClauseEdit />} />
          <Route
            path="/document-tools"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <DocumentTools />
              </Suspense>
            }
          />

          {/* Team - all can view, but actions are role-limited in component */}
          {/* <Route path="/team" element={<Team />} /> */}

          {/* Tools - admin and owner only */}
          <Route
            path="/merge-keys"
            element={
              <AdminOrOwnerGuard>
                <MergeKeys />
              </AdminOrOwnerGuard>
            }
          />
          <Route
            path="/api-keys"
            element={
              <AdminOrOwnerGuard>
                <APIKeys />
              </AdminOrOwnerGuard>
            }
          />
          <Route
            path="/embed"
            element={
              <AdminOrOwnerGuard>
                <Embed />
              </AdminOrOwnerGuard>
            }
          />
          <Route
            path="/integrations"
            element={
              <AdminOrOwnerGuard>
                <Integrations />
              </AdminOrOwnerGuard>
            }
          />
          <Route
            path="/integrations/zapier"
            element={
              <AdminOrOwnerGuard>
                <ZapierSettings />
              </AdminOrOwnerGuard>
            }
          />
          <Route
            path="/integrations/payments"
            element={
              <AdminOrOwnerGuard>
                <PaymentSettings />
              </AdminOrOwnerGuard>
            }
          />
          <Route path="/integrations/ai" element={<AISettings />} />
          <Route
            path="/webhooks"
            element={
              <AdminOrOwnerGuard>
                <Webhooks />
              </AdminOrOwnerGuard>
            }
          />

          {/* Settings Hub */}
          <Route path="/settings" element={<Settings />} />

          {/* Profile Settings */}
          <Route path="/settings/profile" element={<ProfileSettings />} />

          {/* Organisation Settings */}
          <Route
            path="/settings/organisation"
            element={<OrganizationSettings />}
          />

          {/* Team Settings */}
          <Route path="/settings/team" element={<TeamSettings />} />

          {/* Branding Settings */}
          <Route path="/settings/branding" element={<BrandingSettings />} />

          {/* Notification Settings */}
          <Route
            path="/settings/notifications"
            element={<NotificationSettings />}
          />

          {/* Reminders Settings */}
          <Route
            path="/settings/reminders"
            element={
              <AdminOrOwnerGuard>
                <RemindersSettings />
              </AdminOrOwnerGuard>
            }
          />

          {/* SMS Notifications Settings */}
          <Route
            path="/settings/sms"
            element={
              <AdminOrOwnerGuard>
                <SMSSettings />
              </AdminOrOwnerGuard>
            }
          />

          {/* Security Settings */}
          <Route path="/settings/security" element={<SecuritySettings />} />

          {/* SSO Settings */}
          <Route
            path="/settings/sso"
            element={
              <AdminOrOwnerGuard>
                <SSOSettings />
              </AdminOrOwnerGuard>
            }
          />

          {/* Advanced Security Settings */}
          <Route
            path="/settings/advanced-security"
            element={
              <AdminOrOwnerGuard>
                <AdvancedSecuritySettings />
              </AdminOrOwnerGuard>
            }
          />

          {/* Certificates / Digital Signatures Settings */}
          <Route
            path="/settings/certificates"
            element={
              <OwnerFeatureGuard feature="digitalSignatures">
                <CertificatesSettings />
              </OwnerFeatureGuard>
            }
          />

          {/* Data Retention Settings */}
          <Route
            path="/settings/retention"
            element={
              <AdminOrOwnerGuard>
                <DataRetentionSettings />
              </AdminOrOwnerGuard>
            }
          />

          {/* Billing Settings */}
          <Route
            path="/settings/billing"
            element={
              <OwnerGuard>
                <BillingLayout />
              </OwnerGuard>
            }
          >
            {/* Billing Overview (default) */}
            <Route index element={<BillingOverview />} />

            {/* Plans */}
            <Route path="plans" element={<BillingPlans />} />

            {/* Add-ons */}
            <Route path="addons" element={<BillingAddons />} />

            {/* Credits */}
            <Route path="credits" element={<BillingCredits />} />

            {/* Usage */}
            <Route path="usage" element={<BillingUsage />} />

            {/* Invoices */}
            <Route path="invoices" element={<BillingInvoices />} />

            {/* Payment Methods */}
            <Route path="payment-methods" element={<BillingPaymentMethods />} />
          </Route>

          {/* Document Generation - Professional+ plans */}
          <Route
            path="/document-generation"
            element={
              <OwnerFeatureGuard feature="documentGeneration">
                <DocumentGeneration />
              </OwnerFeatureGuard>
            }
          />
          <Route
            path="/document-generation/new"
            element={
              <OwnerFeatureGuard feature="documentGeneration">
                <DocGenTemplateCreate />
              </OwnerFeatureGuard>
            }
          />
          <Route
            path="/document-generation/:id/edit"
            element={
              <OwnerFeatureGuard feature="documentGeneration">
                <DocGenTemplateCreate />
              </OwnerFeatureGuard>
            }
          />
          <Route
            path="/document-generation/:id/generate"
            element={
              <OwnerFeatureGuard feature="documentGeneration">
                <DocGenGenerate />
              </OwnerFeatureGuard>
            }
          />

          {/* Account - admin and owner only */}
          {/* <Route path="/reminders" element={
          <AdminOrOwnerGuard><Reminders /></AdminOrOwnerGuard>
        } /> */}
          {/* <Route path="/branding" element={
          <AdminOrOwnerGuard><BrandingSettings /></AdminOrOwnerGuard>
        } /> */}

          {/* Usage - admin and owner only */}
          <Route
            path="/usage"
            element={
              <AdminOrOwnerGuard>
                <Usage />
              </AdminOrOwnerGuard>
            }
          />

          {/* Admin routes - app owner only (OWNER_EMAIL) */}
          <Route
            path="/admin"
            element={
              <AdminGuard>
                <AdminDashboard />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/users"
            element={
              <AdminGuard>
                <AdminUsers />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/organisations"
            element={
              <AdminGuard>
                <AdminOrganizations />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/invoices"
            element={
              <AdminGuard>
                <AdminInvoices />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/pricing"
            element={
              <AdminGuard>
                <AdminPricing />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/custom-plans"
            element={
              <AdminGuard>
                <AdminCustomPlans />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/security"
            element={
              <AdminGuard>
                <AdminSecurity />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/sms"
            element={
              <AdminGuard>
                <AdminSMS />
              </AdminGuard>
            }
          />
          <Route
            path="/admin/knowledge-base"
            element={
              <AdminGuard>
                <AdminKnowledgeBase />
              </AdminGuard>
            }
          />
          <Route path="/knowledge-base" element={<KnowledgeBase />} />
        </Route>

        {/* Public signing routes */}
        <Route element={<SigningLayout />}>
          <Route
            path="/sign/:token"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <SigningSession />
              </Suspense>
            }
          />
          <Route
            path="/sign/:token/mobile"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <MobileSigningPage />
              </Suspense>
            }
          />
          <Route
            path="/sign/:token/verify"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <SignerVerification />
              </Suspense>
            }
          />
          <Route
            path="/sign/link/:token"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <PublicSigningLink />
              </Suspense>
            }
          />
          <Route
            path="/s/:linkId"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <PublicSigningLink />
              </Suspense>
            }
          />
          <Route
            path="/sign/in-person/:sessionToken"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <InPersonSession />
              </Suspense>
            }
          />
          <Route
            path="/in-person/:sessionToken"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <InPersonSession />
              </Suspense>
            }
          />
          <Route
            path="/in-person/:sessionToken/sign/:recipientId"
            element={
              <Suspense fallback={<PDFLoadingFallback />}>
                <InPersonSignerView />
              </Suspense>
            }
          />
        </Route>

        {/* Protected in-person host route */}
        <Route
          element={
            <ProtectedRoute>
              <SigningLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path="/signing/in-person/:sessionId/host"
            element={<InPersonSigningHost />}
          />
        </Route>

        {/* Public verification routes */}
        <Route path="/verify" element={<Verification />} />
        <Route path="/verify/:envelopeId" element={<Verification />} />

        {/* Public legal pages */}
        {/* <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route
          path="/electronic-signature-disclosure"
          element={<ESignDisclosure />}
        />
        <Route path="/subprocessors" element={<Subprocessors />} /> */}

        {/* Team invitation acceptance - public route */}
        <Route path="/invite/:token" element={<InviteAccept />} />

        {/* Embed routes (no layout - standalone pages) */}
        <Route
          path="/embed/create/:token"
          element={
            <Suspense fallback={<PDFLoadingFallback />}>
              <EmbedCreate />
            </Suspense>
          }
        />
        <Route
          path="/embed/sign/:token"
          element={
            <Suspense fallback={<PDFLoadingFallback />}>
              <EmbedSign />
            </Suspense>
          }
        />
        <Route
          path="/embed/prepare/:token"
          element={
            <Suspense fallback={<PDFLoadingFallback />}>
              <EmbedCreate />
            </Suspense>
          }
        />

        {/* Redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </LiveRegionProvider>
  );
}

export default App;
